import { UserType } from '@bulthaup/api/db';
import { getCombinedCustomerName } from '@bulthaup/api/entities/customer';
import { Button, Group, Stack, Text } from '@mantine/core';
import {
  IconArrowLeft,
  IconFileInvoice,
  IconReceipt,
} from '@tabler/icons-react';
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useLocation,
} from '@tanstack/react-router';

import { useUser } from '../../../hooks/use-user';
import { trpc } from '../../../services/trpc';
import { DeleteProjectButton } from './-components/-delete-project-button';
import { ProjectSiblingsSelector } from './-components/-project-siblings-selector';
import { EditProjectButton } from './-edit-project-button';
import { HeaderTabs } from './-header-tabs';
import { InstallerProjectEditAddressesButton } from './$slug/-components/-installer-project-edit-addresses/-installer-project-edit-addresses-button';
import classes from './projet-layout.module.css';

function ProjectLayout() {
  const { slug } = Route.useParams();
  const { user } = useUser();
  const location = useLocation();
  const isOnFacturationPage = location.pathname.includes('/facturation');
  const isOnAvoirPage = location.pathname.includes('/avoirs');
  const { data: project } = trpc.projects.get.useQuery({
    slug,
  });

  if (!project) return null;

  return (
    <Stack>
      <Stack gap="xs" className={classes.root}>
        <Group>
          <Link to="/app/projets">
            <Button variant="outline" leftSection={<IconArrowLeft />}>
              Retour
            </Button>
          </Link>
          <Text fw={500} size="lg">
            {project?.name} - {getCombinedCustomerName(project.customer)}
          </Text>
          <Group ml="auto">
            <ProjectSiblingsSelector customerId={project.customerId} />
            <Link
              to="/app/projets/$slug/avoirs"
              params={{ slug: project.slug }}
            >
              <Button
                leftSection={<IconFileInvoice size={16} />}
                variant={isOnAvoirPage ? 'light' : 'outline'}
              >
                Avoirs
              </Button>
            </Link>
            <Link
              to="/app/projets/$slug/facturation"
              params={{ slug: project.slug }}
            >
              <Button
                leftSection={<IconReceipt size={16} />}
                variant={isOnFacturationPage ? 'light' : 'outline'}
              >
                Facturation
              </Button>
            </Link>
            {user?.type === UserType.INSTALLER ? (
              <InstallerProjectEditAddressesButton project={project} />
            ) : (
              <EditProjectButton project={project} />
            )}
            {user.type === UserType.ADMIN && (
              <DeleteProjectButton project={project} />
            )}
          </Group>
        </Group>
        <HeaderTabs />
      </Stack>
      <Outlet />
    </Stack>
  );
}

export const Route = createFileRoute('/app/projets/$slug')({
  component: ProjectLayout,
  beforeLoad: async ({ params, matches, context }) => {
    const lastMatch = matches[matches.length - 1];
    if (
      lastMatch.routeId !== '/app/projets/$slug/poseur/' &&
      lastMatch.routeId !== '/app/projets/$slug/client/' &&
      lastMatch.routeId !== '/app/projets/$slug/photos/' &&
      context.user?.type === UserType.INSTALLER
    ) {
      throw redirect({
        to: '/app/projets/$slug/poseur',
        replace: true,
        params: { slug: params.slug },
      });
    }

    // Redirect to /accueil by default
    if (lastMatch.routeId === '/app/projets/$slug') {
      throw redirect({
        to: '/app/projets/$slug/budget',
        replace: true,
        params: { slug: params.slug },
      });
    }
  },
});
